/**
 * Splitting the model's thinking into what to show now and what to fold away.
 *
 * `bimgo-ai-web`'s `ReasoningSection` does this inline; it lives here because
 * the two views are decided by rules, and the rules are the part worth pinning:
 *
 *   - **While it streams, show the newest line.** What the model is thinking
 *     *now* is the only part worth a line of the screen, and it is the thing
 *     that tells a waiting user the turn is alive rather than stuck.
 *   - **Once it is done, show the first line and fold the rest.** Reasoning
 *     opens by saying what it is about to do, so the first line is the summary
 *     the reader wants; everything after it is provenance.
 *
 * Blank lines are dropped throughout. The stream arrives with plenty of them —
 * a reasoning summary is `**Header**\n\n body \n\n**Header**\n\n body` — and
 * "the last line" is useless if it is usually empty.
 */

/** Non-empty lines, in order. */
export function reasoningLines(text: string): string[] {
  return text.split("\n").filter((line) => line.trim().length > 0);
}

/**
 * The line to show while the turn is still running, or null when there is
 * nothing yet and the caller should say "thinking" instead.
 *
 * **Trimmed to the last line, deliberately.** A reasoning summary that has not
 * emitted a newline yet is one growing paragraph, and showing all of it turns a
 * one-line status into a block that reflows the transcript on every token. The
 * cap keeps that case to something a single line can hold.
 */
export function latestReasoningLine(text: string, maxChars = 160): string | null {
  const lines = reasoningLines(text);
  const latest = lines[lines.length - 1];
  if (!latest) return null;

  const clean = latest.trim();
  if (clean.length <= maxChars) return clean;
  // From the end: the newest words are the ones that changed, and a head-anchored
  // truncation would freeze on the same prefix for the whole paragraph.
  return `…${clean.slice(clean.length - maxChars)}`;
}

export interface FoldedReasoning {
  /** The line shown beside the lightbulb. Empty means: render nothing at all. */
  first: string;
  /** Everything else, joined — the body behind the chevron. */
  rest: string;
}

/**
 * The finished shape: a first line, and the remainder to fold.
 *
 * `rest` is empty when there is only one line, and the caller uses that to
 * decide whether the chevron appears — a disclosure that opens onto nothing is
 * worse than no disclosure.
 */
export function foldReasoning(text: string): FoldedReasoning {
  const lines = reasoningLines(text);
  return {
    first: lines[0] ?? "",
    rest: lines.slice(1).join("\n"),
  };
}
